import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft, Camera, CameraOff, Check, Circle, Copy, ExternalLink, Layers, LayoutGrid, Mic, MicOff, MonitorPlay,
  PictureInPicture2, Radio, RefreshCw, Square, SquareUser, Tag, UserMinus, UserPlus, Users, Volume2, X, Columns2, Loader2, Film,
  MessageCircleQuestion, ThumbsUp, CheckCheck, Eye, EyeOff, FlipHorizontal2, RotateCw, Maximize2, Minimize2, Settings2, Smartphone, QrCode, ScanLine,
  Zap, AlignLeft, AlignCenter, AlignRight, BookmarkCheck, Tv, Timer, WifiOff, Lock, LockOpen, Hand, Hourglass,
} from "lucide-react";
import { RtkParticipantsAudio } from "@cloudflare/realtimekit-react-ui";
import ProgramCanvas from "@/components/studio/ProgramCanvas";
import VideoTile, { trackSize } from "@/components/studio/VideoTile";
import { EditShowDialog } from "@/components/studio/EditShowDialog";
import { HoldingTextEditor } from "@/components/studio/HoldingTextEditor";
import { PreShowChecklist } from "@/components/studio/PreShowChecklist";
import { SimulcastPanel } from "@/components/studio/SimulcastPanel";
import { AudioMixer } from "@/components/studio/AudioMixer";
import { useRtkMeeting, useRoster, useStableSpeaker, useConnectionHealth, useUplinkStats } from "@/hooks/useRtkMeeting";
import ConnectionDot from "@/components/studio/ConnectionDot";
import InTheRoom from "@/components/studio/InTheRoom";
import ProgramSketch from "@/components/studio/ProgramSketch";
import MonitorHealth from "@/components/studio/MonitorHealth";
import { useElapsed } from "@/hooks/useElapsed";
import { useShowSocket } from "@/hooks/useShowSocket";
import { studioApi, errMessage, producerClientId } from "@/lib/studioApi";
import { API_BASE } from "@/lib/api";

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */
const LAYOUTS = [
  { id: "solo", label: "Solo", Icon: SquareUser, max: 1 },
  { id: "split", label: "Split", Icon: Columns2, max: 2 },
  { id: "grid", label: "Grid", Icon: LayoutGrid, max: 6 },
  { id: "pip", label: "PiP", Icon: PictureInPicture2, max: 2 },
  { id: "speaker", label: "Auto speaker", Icon: Volume2, max: 6 },
];

function copyText(text, label = "Link") {
  navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => toast.error("Could not copy"));
}

const Pill = ({ tone = "zinc", children, testId }) => {
  const tones = {
    zinc: "bg-white/5 text-zinc-300 border-white/10",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    copper: "bg-[#c9772e]/15 text-[#e6a25f] border-[#c9772e]/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  };
  return <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${tones[tone]}`}>{children}</span>;
};

const Btn = ({ tone = "ghost", className = "", children, ...rest }) => {
  const tones = {
    ghost: "bg-white/5 hover:bg-white/10 text-zinc-100 border-white/10",
    copper: "bg-[#c9772e] hover:bg-[#d98a3f] text-white border-transparent",
    red: "bg-red-600 hover:bg-red-500 text-white border-transparent",
    dark: "bg-zinc-900 hover:bg-zinc-800 text-zinc-100 border-white/10",
    outline: "bg-transparent hover:bg-white/5 text-zinc-200 border-white/15",
  };
  return (
    <button type="button" {...rest} className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${tones[tone]} ${className}`}>
      {children}
    </button>
  );
};

const Section = ({ title, right, children, testId }) => (
  <section className="rounded-xl border border-white/10 bg-[#0f1520]" data-testid={testId}>
    <header className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
      <h3 className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{title}</h3>
      {right}
    </header>
    <div className="p-4">{children}</div>
  </section>
);

/* ------------------------------------------------------------------ */
/* Console                                                             */
/* ------------------------------------------------------------------ */
export default function ProducerConsole() {
  const { showId } = useParams();
  const navigate = useNavigate();
  const [secrets, setSecrets] = useState(null); // producer-only fields (tokens, paths)
  const [initialShow, setInitialShow] = useState(null);
  const [hostToken, setHostToken] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState({});
  const sessionToken = typeof window !== "undefined" ? localStorage.getItem("fc_token") : "";

  const [questionsTick, setQuestionsTick] = useState(0);
  // Producer control: one computer produces a show at a time. `mine` = this browser holds it.
  const myClientId = producerClientId();
  const [mine, setMine] = useState(false);
  const [controlBusy, setControlBusy] = useState(false);
  const mineRef = useRef(false);
  mineRef.current = mine;
  const onShowEvent = useCallback((msg) => {
    if (msg.type === "questions_changed") setQuestionsTick((t) => t + 1);
    if (msg.type === "control_changed") {
      const c = msg.control;
      if (c && c.client_id !== myClientId && mineRef.current) {
        setMine(false);
        toast.warning(`${c.name || "Another producer"} took control from ${c.device || "another computer"}`, { description: "This console is now view-only. Use “Take control” to take it back." });
      }
    }
  }, [myClientId]);
  const { show: liveShow, setShow } = useShowSocket(showId, { token: sessionToken }, undefined, onShowEvent);
  const show = liveShow || initialShow;
  const [rtkSession, setRtkSession] = useState(0);
  const { meeting, joined, error: rtkError } = useRtkMeeting(hostToken, { audio: mine, video: mine, audioProfile: "studio", session: rtkSession });
  const roster = useRoster(meeting);

  // Media link watchdog: if RealtimeKit reports the media connection failed / stayed disconnected
  // for 15 s (symptom: preview goes blank while the recorder and room monitors are fine),
  // rebuild this console's connection with a fresh client. Also offered as a button.
  const [mediaState, setMediaState] = useState("connected");
  useEffect(() => {
    if (!meeting) return undefined;
    const onNet = ({ state }) => setMediaState(state || "connected");
    meeting.meta?.on?.("mediaConnectionUpdate", onNet);
    return () => { try { meeting.meta?.off?.("mediaConnectionUpdate", onNet); } catch (e) { /* noop */ } };
  }, [meeting]);
  const resync = useCallback((why = "manual") => {
    setMediaState("connected");
    setRtkSession((s) => s + 1);
    toast.message(why === "auto" ? "Studio link dropped — reconnecting the console" : "Reconnecting the console to the room…", { description: "Recording, stream and room monitors are not affected." });
  }, []);
  useEffect(() => {
    if (!["failed", "disconnected"].includes(mediaState)) return undefined;
    const id = setTimeout(() => resync("auto"), 15000);
    return () => clearTimeout(id);
  }, [mediaState, resync]);

  // Safety net: our app runs its own lobby, so anyone RealtimeKit parks in ITS
  // waiting room (preset with a waiting room) is let straight in by the host.
  useEffect(() => {
    if (!meeting || !joined) return undefined;
    const accept = (p) => { try { meeting.participants.acceptWaitingRoomRequest([p.id]); } catch (e) { /* noop */ } };
    try { meeting.participants.waitlisted.toArray().forEach(accept); } catch (e) { /* noop */ }
    meeting.participants.waitlisted.on("participantJoined", accept);
    return () => { try { meeting.participants.waitlisted.off("participantJoined", accept); } catch (e) { /* noop */ } };
  }, [meeting, joined]);

  // Load show, claim control (first console wins), then join the room: as host with camera + mic
  // when in control, otherwise as a hidden viewer (no second host source, no echo in the room).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await studioApi.getShow(showId);
        if (cancelled) return;
        setSecrets(s);
        setInitialShow(s);
        if (s.status === "ended") return;
        let inControl = false;
        try { await studioApi.claimControl(showId, false); inControl = true; }
        catch (e) { if (e?.response?.status !== 409) throw e; }
        if (cancelled) return;
        setMine(inControl);
        const t = await studioApi.hostToken(showId, !inControl);
        if (!cancelled) setHostToken(t.auth_token);
      } catch (e) {
        if (!cancelled) setLoadError(errMessage(e, "Could not open this show"));
      }
    })();
    return () => { cancelled = true; };
  }, [showId]);

  // Keep control alive while we hold it; a 409 means another console took over.
  const dropToViewOnly = useCallback(async (why) => {
    setMine(false);
    if (why) toast.warning(why, { description: "This console is now view-only." });
    try { const t = await studioApi.hostToken(showId, true); setHostToken(t.auth_token); } catch (e) { /* keep current session */ }
  }, [showId]);
  useEffect(() => {
    if (!mine) return undefined;
    const beat = () => studioApi.controlHeartbeat(showId).catch((e) => { if (e?.response?.status === 409) dropToViewOnly(e.response?.headers?.["x-control-holder"] ? `${e.response.headers["x-control-holder"]} is now in control` : "Another console is now in control"); });
    const id = setInterval(beat, 15000);
    document.addEventListener("visibilitychange", beat);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", beat); };
  }, [mine, showId, dropToViewOnly]);
  // Snapshot says someone else holds control while we think we do -> we lost it.
  const holder = show?.control || null;
  useEffect(() => {
    if (mine && holder && holder.client_id !== myClientId) dropToViewOnly(`${holder.name || "Another producer"} took control from ${holder.device || "another computer"}`);
  }, [holder, mine, myClientId, dropToViewOnly]);
  // Hand control back when this console closes (tab close uses a keepalive request).
  useEffect(() => {
    const bye = () => {
      if (!mineRef.current) return;
      try {
        fetch(`${API_BASE}/studio/shows/${showId}/control/release`, {
          method: "POST", keepalive: true, credentials: "include",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("fc_token") || ""}`, "X-Producer-Client": myClientId },
          body: JSON.stringify({ client_id: myClientId }),
        });
      } catch (e) { /* noop */ }
    };
    window.addEventListener("pagehide", bye);
    return () => { window.removeEventListener("pagehide", bye); bye(); };
  }, [showId, myClientId]);
  const takeControl = async () => {
    setControlBusy(true);
    try {
      await studioApi.claimControl(showId, true);
      setMine(true);
      const t = await studioApi.hostToken(showId, false);
      setHostToken(t.auth_token);
      toast.success("You're in control", { description: "Your camera and mic are joining the room." });
    } catch (e) { toast.error(errMessage(e, "Could not take control")); }
    finally { setControlBusy(false); }
  };

  const program = show?.program;
  const participants = useMemo(() => show?.participants || [], [show?.participants]);
  const registry = useMemo(() => new Map(participants.map((p) => [p.participant_id, p])), [participants]);
  // Light mode (slow laptops): no video preview, no audio meters. Remembered per browser.
  const [light, setLight] = useState(() => typeof window !== "undefined" && localStorage.getItem("fc_console_light") === "1");
  useEffect(() => { localStorage.setItem("fc_console_light", light ? "1" : "0"); }, [light]);
  const activeSpeaker = useStableSpeaker(meeting, roster, participants, light ? null : program);
  const lobby = participants.filter((p) => p.status === "lobby");
  const admitted = participants.filter((p) => p.status === "admitted");

  const rec = show?.recording || {};
  const live = show?.livestream || {};
  const recActive = ["starting", "recording"].includes(rec.status);
  const recBusy = ["uploading"].includes(rec.status);
  const liveActive = ["starting", "live"].includes(live.status);
  const recTimer = useElapsed(rec.started_at, recActive);
  const liveTimer = useElapsed(live.started_at, liveActive);
  const ended = show?.status === "ended";
  const locked = ended || !mine; // view-only consoles can look but not touch

  const run = useCallback(async (key, fn, okMsg) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const res = await fn();
      if (okMsg) toast.success(okMsg);
      return res;
    } catch (e) {
      toast.error(errMessage(e));
      return null;
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  }, []);

  const patchProgram = useCallback((patch) => run("program", async () => {
    const prog = await studioApi.updateProgram(showId, patch);
    setShow((s) => (s ? { ...s, program: prog } : s));
    return prog;
  }), [showId, run, setShow]);

  /* ---- source selection ---- */
  const layoutMax = LAYOUTS.find((l) => l.id === program?.layout)?.max || 1;
  const onTileClick = (pid) => {
    if (!program) return;
    const cur = program.sources || [];
    if (layoutMax === 1) return patchProgram({ sources: [pid] });
    if (cur.includes(pid)) return patchProgram({ sources: cur.filter((x) => x !== pid) });
    const next = [...cur, pid];
    return patchProgram({ sources: next.length > layoutMax ? next.slice(next.length - layoutMax) : next });
  };
  const toggleLowerThird = (pid, pin = false) => {
    const lt = program?.lower_third || {};
    // Only a pinned plate toggles off. A plate that is merely auto-flashing (e.g. right after a
    // cut) gets re-flashed / pinned instead — otherwise clicking "name plate" during those few
    // seconds silently hid it, which felt like the tag working only half the time.
    if (lt.visible && lt.participant_id === pid && !lt.auto) return patchProgram({ lower_third: { visible: false } });
    if (pin) return patchProgram({ lower_third: { visible: true, participant_id: pid } });
    return patchProgram({ lower_third: { flash: true, participant_id: pid } });
  };

  /* ---- hotkeys: 1-9 take a source, 0 = holding card, N = name plate for the main source ---- */
  useEffect(() => {
    const h = (e) => {
      if (locked || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target?.isContentEditable) return;
      if (e.key === "0") { patchProgram({ sources: [] }); return; }
      if (e.key === "n" || e.key === "N") {
        const main = program?.sources?.[0];
        if (main) toggleLowerThird(main, e.shiftKey);
        return;
      }
      const n = parseInt(e.key, 10);
      if (!n || n > 9) return;
      const src = roster.list[n - 1];
      if (src?.customId) onTileClick(src.customId);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  /* ---- job controls ---- */
  const onRecord = () => run("rec", async () => {
    if (recActive) { const r = await studioApi.stopRecording(showId); setShow((s) => ({ ...s, recording: r })); toast.success("Recording stopped — processing"); }
    else { const r = await studioApi.startRecording(showId); setShow((s) => ({ ...s, recording: r })); toast.success("Recording started"); }
  });
  const onLive = () => run("live", async () => {
    if (liveActive) { const r = await studioApi.stopLive(showId); setShow((s) => ({ ...s, livestream: r })); toast.success("Stream ended"); }
    else {
      const r = await studioApi.startLive(showId); setShow((s) => ({ ...s, livestream: r })); toast.success("Going live — the Hub will switch over in ~20 seconds");
      (r.simulcast || []).forEach((d) => (d.ok ? toast.success(`Also streaming to ${d.label}`) : toast.error(`${d.label}: ${d.error || "couldn't start"}`)));
    }
  });
  const onEnd = () => {
    if (!window.confirm("End the show for everyone? This stops recording and streaming and closes the room.")) return;
    run("end", async () => { await studioApi.endShow(showId); toast.success("Show ended"); navigate("/studio"); });
  };

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0f17] text-zinc-100">
        <div className="text-center">
          <p className="text-lg font-semibold">{loadError}</p>
          <Link to="/studio" className="mt-4 inline-block text-[#e6a25f] underline">Back to Studio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f17] text-zinc-100" data-testid="producer-console">
      {/* Remote audio for the producer (hear guests). The program preview is muted. */}
      {meeting && <RtkParticipantsAudio meeting={meeting} />}

      {/* Top bar */}
      <header className="sticky top-0 z-20 flex flex-wrap items-center gap-3 border-b border-white/10 bg-[#0a0f17]/95 px-4 py-2.5 backdrop-blur">
        <Link to="/studio" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white" data-testid="console-back"><ArrowLeft className="h-4 w-4" /> Studio</Link>
        <div className="mx-1 h-5 w-px bg-white/10" />
        <EditShowDialog show={show} onSaved={(res) => setShow((s) => (s ? { ...s, title: res.title, scheduled_for: res.scheduled_for } : s))} />
        <div className="flex items-center gap-2">
          {ended && <Pill tone="zinc" testId="pill-ended">Ended</Pill>}
          {recActive && <Pill tone="red" testId="pill-rec"><Circle className="h-2.5 w-2.5 fill-current animate-pulse" /> REC {recTimer}</Pill>}
          {recBusy && <Pill tone="amber" testId="pill-processing"><Loader2 className="h-3 w-3 animate-spin" /> Processing recording</Pill>}
          {rec.status === "imported" && <Pill tone="green" testId="pill-saved"><Check className="h-3 w-3" /> Recording saved</Pill>}
          {liveActive && <Pill tone="copper" testId="pill-live"><Radio className="h-3 w-3" /> LIVE {liveTimer}</Pill>}
          {liveActive && <BroadcastDelayPill showId={showId} />}
          {show?.program_connected && (recActive || liveActive) && <Pill tone="green" testId="pill-recorder">Recorder connected</Pill>}
          {meeting && !joined && <Pill tone="amber">Connecting to room…</Pill>}
          {!ended && hostToken && (mine
            ? <Pill tone="green" testId="pill-control"><Lock className="h-3 w-3" /> In control</Pill>
            : <Pill tone="amber" testId="pill-view-only"><LockOpen className="h-3 w-3" /> View only</Pill>)}
          {["failed", "disconnected", "reconnecting"].includes(mediaState) && <Pill tone="amber" testId="pill-media-trouble"><WifiOff className="h-3 w-3" /> Studio link unstable</Pill>}
          {!ended && hostToken && (
            <button type="button" onClick={() => resync("manual")} title="Preview blank or frozen while monitors are fine? Rebuild this console's connection to the room. Your own camera/mic re-publish within a few seconds; recording, stream and monitors keep running." className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[11px] text-zinc-400 hover:bg-white/10 hover:text-zinc-100" data-testid="console-resync">
              <RefreshCw className="h-3 w-3" /> Re-sync
            </button>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Btn tone={recActive ? "dark" : "red"} onClick={onRecord} disabled={locked || busy.rec || recBusy || !joined} data-testid="btn-record">
            {busy.rec ? <Loader2 className="h-4 w-4 animate-spin" /> : recActive ? <Square className="h-4 w-4 fill-current" /> : <Circle className="h-4 w-4 fill-current" />}
            {recActive ? "Stop recording" : "Record"}
          </Btn>
          <Btn tone={liveActive ? "dark" : "copper"} onClick={onLive} disabled={locked || busy.live || !joined} data-testid="btn-live">
            {busy.live ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />}
            {liveActive ? "End stream" : "Go live"}
          </Btn>
          <Btn tone="outline" onClick={onEnd} disabled={locked || busy.end} data-testid="btn-end">End show</Btn>
        </div>
      </header>

      {rtkError && <div className="mx-4 mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-200" data-testid="rtk-error">{rtkError}</div>}

      {!ended && hostToken && !mine && (
        <div className="mx-4 mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-100" data-testid="control-banner">
          <LockOpen className="h-4 w-4 shrink-0 text-amber-300" />
          <span className="min-w-0 flex-1" data-testid="control-banner-text">
            {holder && holder.client_id !== myClientId
              ? <><span className="font-semibold">{holder.name || "Another producer"}</span> is producing this show from <span className="font-semibold">{holder.device || "another computer"}</span>. This console is view-only — you can watch the program, sources and questions, but only one computer can switch, tag and record at a time.</>
              : <>Nobody is in control of this show right now. Take control to switch, tag and record from this computer.</>}
          </span>
          <Btn tone="copper" className="px-3 py-1.5 text-xs" onClick={takeControl} disabled={controlBusy} data-testid="btn-take-control">
            {controlBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Hand className="h-3.5 w-3.5" />} Take control
          </Btn>
        </div>
      )}

      <main className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_400px]">
        {/* ---------------- Program column ---------------- */}
        <div className="space-y-4">
          <div className="overflow-hidden rounded-xl border border-white/10 bg-black shadow-2xl" data-testid="program-monitor">
            <div className="flex items-center justify-between border-b border-white/10 bg-[#0f1520] px-4 py-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400"><MonitorPlay className="h-4 w-4 text-[#e6a25f]" /> Program</div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setLight((v) => !v)} title={light ? "Light mode is on: no video preview and no audio meters, so a slow laptop keeps up. Click to show the live preview again." : "Turn on Light mode for a slow laptop: swaps the live preview for a layout sketch and pauses the audio meters. Recording, stream and room monitors are unaffected."}
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition ${light ? "border-[#c9772e] bg-[#c9772e]/15 text-[#e6a25f]" : "border-white/10 text-zinc-400 hover:bg-white/10 hover:text-zinc-200"}`} data-testid="toggle-light-mode" aria-pressed={light}>
                  <Zap className="h-3.5 w-3.5" /> Light mode{light ? " on" : ""}
                </button>
                {secrets?.program_path && (
                  <a href={`${window.location.origin}${secrets.program_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid="open-program-output"><ExternalLink className="h-3.5 w-3.5" /> Open output page</a>
                )}
              </div>
            </div>
            <div className="aspect-video w-full">
              {light
                ? <ProgramSketch program={program} roster={roster} registry={registry} showTitle={show?.title} className="h-full w-full" />
                : <ProgramCanvas meeting={meeting} roster={roster} activeSpeaker={activeSpeaker} program={program} participants={participants} showTitle={show?.title} scheduledFor={show?.scheduled_for} className="h-full w-full" />}
            </div>
          </div>

          <InTheRoom show={show} program={program} patchProgram={patchProgram} disabled={locked} />

          <div className="grid gap-4 md:grid-cols-2">
            <Section title="Layout" testId="layout-section">
              <div className="grid grid-cols-5 gap-2">
                {LAYOUTS.map(({ id, label, Icon }) => (
                  <button key={id} type="button" onClick={() => patchProgram({ layout: id })} disabled={locked} data-testid={`layout-${id}`}
                    className={`flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-[11px] font-medium transition ${program?.layout === id ? "border-[#c9772e] bg-[#c9772e]/15 text-[#e6a25f]" : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
                    <Icon className="h-5 w-5" />{label}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => patchProgram({ sources: [] })} disabled={locked || !(program?.sources || []).length} data-testid="btn-holding-card"
                className={`mt-2 flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition disabled:cursor-default ${!(program?.sources || []).length ? "border-[#c9772e] bg-[#c9772e]/15 text-[#e6a25f]" : "border-white/10 bg-white/5 text-zinc-300 hover:bg-white/10"}`}>
                <Hourglass className="h-4 w-4 shrink-0" />
                <span className="flex-1">Starting-soon screen <span className="font-normal text-zinc-500">· takes every camera off air</span></span>
                {!(program?.sources || []).length ? <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">ON AIR</span> : <kbd className="rounded border border-white/15 px-1 text-[10px] text-zinc-500">0</kbd>}
              </button>
              <HoldingTextEditor program={program} showTitle={show?.title} scheduledFor={show?.scheduled_for} patchProgram={patchProgram} disabled={locked} />
              <p className="mt-3 text-xs text-zinc-500">{layoutMax === 1 ? "Click a source to put it on air." : `Click sources to add them (up to ${layoutMax}). Click again to remove.`} <span className="text-zinc-600">Keys <kbd className="rounded border border-white/15 px-1">1</kbd>–<kbd className="rounded border border-white/15 px-1">9</kbd> take a source, <kbd className="rounded border border-white/15 px-1">0</kbd> starting-soon screen.</span></p>
              {program?.layout === "speaker" && <SpeakerControls program={program} patchProgram={patchProgram} participants={participants} disabled={locked} />}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-xs text-zinc-300"><input type="checkbox" className="accent-[#c9772e]" checked={program?.show_logo !== false} disabled={locked} onChange={(e) => patchProgram({ show_logo: e.target.checked })} data-testid="toggle-logo" /> Logo</label>
                <label className="flex items-center gap-2 text-xs text-zinc-300"><input type="checkbox" className="accent-[#c9772e]" checked={!!program?.show_title} disabled={locked} onChange={(e) => patchProgram({ show_title: e.target.checked })} data-testid="toggle-title" /> Show title</label>
                <select value={program?.theme || "copper"} disabled={locked} onChange={(e) => patchProgram({ theme: e.target.value })} className="ml-auto rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1 text-xs text-zinc-200" data-testid="select-theme">
                  <option value="copper">Copper theme</option>
                  <option value="midnight">Midnight theme</option>
                  <option value="clean">Clean theme</option>
                </select>
              </div>
            </Section>

            <OverlaysSection program={program} patchProgram={patchProgram} registry={registry} disabled={locked} />
          </div>

          <QuestionsPanel showId={showId} show={show} program={program} tick={questionsTick} run={run} setShow={setShow} disabled={locked} />

          <RecordingsSection show={show} secrets={secrets} onRefresh={() => run("refresh", async () => { const s = await studioApi.refresh(showId); setShow(s); setSecrets(s); })} busy={busy.refresh} />
        </div>

        {/* ---------------- Sources column ---------------- */}
        <div className="space-y-4">
          {!ended && <PreShowChecklist joined={joined} roster={roster} program={program} participants={participants} show={show} rtkError={rtkError} />}
          <Section title={`Sources (${roster.list.length})`} testId="sources-section" right={<span className="text-[11px] text-zinc-500">{admitted.length} admitted</span>}>
            <SourcesGrid roster={roster} registry={registry} program={program} onTileClick={onTileClick} onNamePlate={toggleLowerThird} patchProgram={patchProgram} meeting={meeting} showId={showId} setShow={setShow} run={run} disabled={locked} />
          </Section>

          {!ended && (
            <Section title="Audio mixer" testId="audio-section" right={<span className="text-[11px] text-zinc-500">{light ? "meters paused · Light mode" : `${roster.list.filter((e) => e.rtk?.audioEnabled).length} live`}</span>}>
              <AudioMixer meeting={meeting} joined={joined} roster={roster} registry={registry} program={program} showId={showId} disabled={locked} meters={!light} />
            </Section>
          )}

          <CameraInvite secrets={secrets} showTitle={show?.title} cameraCount={admitted.filter((p) => p.kind === "camera").length} />
          {!ended && <MonitorInvite secrets={secrets} setSecrets={setSecrets} showId={showId} showTitle={show?.title} run={run} />}

          {lobby.length > 0 && (
            <Section title={`Lobby (${lobby.length})`} testId="lobby-section">
              <ul className="space-y-2">
                {lobby.map((p) => (
                  <li key={p.participant_id} className="flex items-center gap-3 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2" data-testid={`lobby-${p.participant_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{p.name}</div>
                      <div className="truncate text-xs text-zinc-400">{[p.title, p.org].filter(Boolean).join(" · ") || "Guest"}</div>
                    </div>
                    <Btn tone="copper" className="px-2.5 py-1.5 text-xs" disabled={locked} onClick={() => run(`admit-${p.participant_id}`, () => studioApi.admit(showId, p.participant_id), `${p.name} admitted`)} data-testid={`admit-${p.participant_id}`}><UserPlus className="h-3.5 w-3.5" /> Admit</Btn>
                    <Btn tone="ghost" className="px-2 py-1.5 text-xs" disabled={locked} onClick={() => run(`decline-${p.participant_id}`, () => studioApi.decline(showId, p.participant_id))} data-testid={`decline-${p.participant_id}`}><X className="h-3.5 w-3.5" /></Btn>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {mine && <SelfControls meeting={meeting} joined={joined} />}
          {!ended && <SimulcastPanel liveId={show?.playback?.live_id} live={liveActive} />}

          <InvitePanel secrets={secrets} showTitle={show?.title} />
        </div>
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Auto-speaker switching controls                                     */
/* ------------------------------------------------------------------ */
const SPEEDS = [
  { hold: 1.5, label: "Snappy", hint: "cuts after 1.5 s" },
  { hold: 3, label: "Normal", hint: "cuts after 3 s" },
  { hold: 5, label: "Relaxed", hint: "cuts after 5 s" },
];

function SpeakerControls({ program, patchProgram, participants, disabled }) {
  const sp = program?.speaker || {};
  const hold = sp.hold ?? 3;
  const hasRoomCam = participants.some((p) => p.room_cam && p.status === "admitted");
  const seg = (active) => `rounded px-2.5 py-1 text-[11px] font-medium transition ${active ? "bg-[#c9772e]/20 text-[#e6a25f]" : "text-zinc-400 hover:bg-white/10 hover:text-zinc-200"}`;
  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid="speaker-controls">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold text-zinc-300">Switching speed</span>
        <div className="flex items-center gap-0.5 rounded-md border border-white/10 p-0.5" data-testid="speaker-speed">
          {SPEEDS.map((s) => (
            <button key={s.hold} type="button" disabled={disabled} title={`Hold each shot at least ${s.hold} s (${s.hint})`} onClick={() => patchProgram({ speaker: { hold: s.hold } })}
              className={seg(Math.abs(hold - s.hold) < 0.01)} data-testid={`speaker-speed-${s.label.toLowerCase()}`}>{s.label}</button>
          ))}
        </div>
        <label className="ml-auto flex items-center gap-2 text-xs text-zinc-300" title={hasRoomCam ? "After ~8 s of nobody talking, cut to the room camera" : "Mark a source as Room cam (Owl) to use this"}>
          <input type="checkbox" className="accent-[#c9772e]" checked={sp.wide_fallback !== false} disabled={disabled} onChange={(e) => patchProgram({ speaker: { wide_fallback: e.target.checked } })} data-testid="speaker-wide-fallback" />
          Wide shot when quiet{!hasRoomCam && <span className="text-zinc-600">(no room cam)</span>}
        </label>
      </div>
      <p className="mt-2 text-[11px] text-zinc-500">A new voice has to carry for about a second before we cut, then the shot is held for the time above. Room cameras never trigger a cut — their mic hears everyone.</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Overlays                                                            */
/* ------------------------------------------------------------------ */
function OverlaysSection({ program, patchProgram, registry, disabled }) {
  const lt = program?.lower_third || {};
  const [banner, setBanner] = useState(program?.banner?.text || "");
  const [q, setQ] = useState(program?.question?.text || "");
  const [qAuthor, setQAuthor] = useState(program?.question?.author || "");
  const [ltName, setLtName] = useState(lt.name || "");
  const [ltTitle, setLtTitle] = useState(lt.title || "");
  const [ltOrg, setLtOrg] = useState(lt.org || "");
  const lastLt = useRef("");
  useEffect(() => {
    const sig = `${lt.participant_id}|${lt.name}|${lt.title}|${lt.org}`;
    if (sig !== lastLt.current) { lastLt.current = sig; setLtName(lt.name || ""); setLtTitle(lt.title || ""); setLtOrg(lt.org || ""); }
  }, [lt.participant_id, lt.name, lt.title, lt.org]);
  const pq = program?.question || {};
  const lastQid = useRef(null);
  useEffect(() => {
    if (pq.question_id) { setQ(pq.text || ""); setQAuthor(pq.author || ""); }
    else if (lastQid.current) { setQ(""); setQAuthor(""); }
    lastQid.current = pq.question_id || null;
  }, [pq.question_id, pq.text, pq.author]);

  const subject = lt.participant_id ? registry.get(lt.participant_id) : null;
  const plate = program?.plate_style || {};
  const auto = program?.plate_auto || {};
  const [autoSecs, setAutoSecs] = useState(auto.seconds || 6);
  useEffect(() => { if (auto.seconds) setAutoSecs(auto.seconds); }, [auto.seconds]);
  const canShow = !!(ltName || lt.name);
  const seg = (active) => `rounded px-2 py-1 text-[11px] font-medium transition ${active ? "bg-[#c9772e]/20 text-[#e6a25f]" : "text-zinc-400 hover:bg-white/10 hover:text-zinc-200"}`;
  return (
    <Section title="Overlays" testId="overlays-section">
      {/* Lower third */}
      <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs font-semibold text-zinc-300"><Tag className="h-3.5 w-3.5 text-[#e6a25f]" /> Name plate {subject && <span className="text-zinc-500">· {subject.name}</span>}
            {lt.visible && <span className="rounded bg-[#c9772e] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" data-testid="lt-status">{lt.auto ? `on screen · ${auto.seconds || 6}s` : "pinned"}</span>}
          </div>
          <div className="flex items-center gap-1.5">
            <Btn tone="ghost" className="px-2.5 py-1 text-xs" disabled={disabled || !canShow} data-testid="flash-lower-third" title={`Show for ${auto.seconds || 6} seconds (key N)`}
              onClick={() => patchProgram({ lower_third: { flash: true, name: ltName, title: ltTitle, org: ltOrg } })}>
              <Zap className="h-3.5 w-3.5" /> Flash
            </Btn>
            <Btn tone={lt.visible && !lt.auto ? "copper" : "ghost"} className="px-2.5 py-1 text-xs" disabled={disabled || !canShow} data-testid="toggle-lower-third" title="Keep on screen until you hide it (shift+N)"
              onClick={() => patchProgram({ lower_third: { visible: !(lt.visible && !lt.auto), name: ltName, title: ltTitle, org: ltOrg } })}>
              {lt.visible && !lt.auto ? "Hide" : lt.visible ? "Keep on" : "Show"}
            </Btn>
          </div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <input value={ltName} onChange={(e) => setLtName(e.target.value)} onBlur={() => patchProgram({ lower_third: { name: ltName } })} placeholder="Name" className="rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="lt-name" />
          <input value={ltTitle} onChange={(e) => setLtTitle(e.target.value)} onBlur={() => patchProgram({ lower_third: { title: ltTitle } })} placeholder="Title" className="rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="lt-title" />
          <input value={ltOrg} onChange={(e) => setLtOrg(e.target.value)} onBlur={() => patchProgram({ lower_third: { org: ltOrg } })} placeholder="Organization" className="rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="lt-org" />
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/5 pt-2.5">
          <select value={plate.style || "classic"} onChange={(e) => patchProgram({ plate_style: { style: e.target.value } })} disabled={disabled} className="rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1 text-[11px] text-zinc-200" data-testid="plate-style">
            <option value="classic">Classic bar</option>
            <option value="broadcast">Broadcast blocks</option>
            <option value="pill">Pill</option>
            <option value="minimal">Minimal text</option>
          </select>
          <div className="flex items-center gap-0.5 rounded-md border border-white/10 p-0.5" data-testid="plate-position">
            {[["left", AlignLeft, "Left"], ["center", AlignCenter, "Center"], ["right", AlignRight, "Right"]].map(([v, Icon, t]) => (
              <button key={v} type="button" title={t} onClick={() => patchProgram({ plate_style: { position: v } })} disabled={disabled} className={seg((plate.position || "left") === v)} data-testid={`plate-pos-${v}`}><Icon className="h-3.5 w-3.5" /></button>
            ))}
          </div>
          <div className="flex items-center gap-0.5 rounded-md border border-white/10 p-0.5" data-testid="plate-size">
            {[["normal", "A"], ["large", "A+"]].map(([v, t]) => (
              <button key={v} type="button" onClick={() => patchProgram({ plate_style: { size: v } })} disabled={disabled} className={seg((plate.size || "normal") === v)} data-testid={`plate-size-${v}`}>{t}</button>
            ))}
          </div>
          <label className="ml-auto flex items-center gap-1.5 text-[11px] text-zinc-300">
            <input type="checkbox" className="accent-[#c9772e]" checked={auto.enabled !== false} disabled={disabled} onChange={(e) => patchProgram({ plate_auto: { enabled: e.target.checked } })} data-testid="plate-auto" />
            Auto-show on switch for
            <input type="number" min={3} max={30} value={autoSecs} disabled={disabled || auto.enabled === false} onChange={(e) => setAutoSecs(e.target.value)}
              onBlur={() => { const s = Math.min(30, Math.max(3, parseInt(autoSecs, 10) || 6)); setAutoSecs(s); if (s !== auto.seconds) patchProgram({ plate_auto: { seconds: s } }); }}
              className="w-12 rounded-md border border-white/10 bg-[#0a0f17] px-1.5 py-0.5 text-center text-[11px]" data-testid="plate-auto-seconds" /> s
          </label>
        </div>
        <p className="mt-1.5 text-[11px] text-zinc-500">Tag button on a source: click = flash their plate, shift-click = keep it on. Details you edit are saved to the plate library for next time.</p>
      </div>

      {/* Banner */}
      <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold text-zinc-300"><Layers className="h-3.5 w-3.5 text-[#e6a25f]" /> Banner</div>
          <Btn tone={program?.banner?.visible ? "copper" : "ghost"} className="px-2.5 py-1 text-xs" disabled={disabled || !banner.trim()} data-testid="toggle-banner"
            onClick={() => patchProgram({ banner: { visible: !program?.banner?.visible, text: banner } })}>
            {program?.banner?.visible ? "Hide" : "Show"}
          </Btn>
        </div>
        <input value={banner} onChange={(e) => setBanner(e.target.value)} onBlur={() => program?.banner?.visible && patchProgram({ banner: { text: banner } })} placeholder="e.g. Today's topic: Generosity in Spokane" className="mt-2 w-full rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="banner-text" />
      </div>

      {/* Question card */}
      <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold text-zinc-300"><Users className="h-3.5 w-3.5 text-[#e6a25f]" /> Audience question</div>
          <Btn tone={program?.question?.visible ? "copper" : "ghost"} className="px-2.5 py-1 text-xs" disabled={disabled || !q.trim()} data-testid="toggle-question"
            onClick={() => patchProgram({ question: { visible: !program?.question?.visible, text: q, author: qAuthor } })}>
            {program?.question?.visible ? "Hide" : "Show"}
          </Btn>
        </div>
        <div className="mt-2 grid grid-cols-[1fr_120px] gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Question text" className="rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="question-text" />
          <input value={qAuthor} onChange={(e) => setQAuthor(e.target.value)} placeholder="From" className="rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="question-author" />
        </div>
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Sources                                                             */
/* ------------------------------------------------------------------ */
function SourcesGrid({ roster, registry, program, onTileClick, onNamePlate, patchProgram, meeting, showId, setShow, run, disabled }) {
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null); // pid with the adjust panel open
  const [, tick] = useState(0);
  const health = useConnectionHealth(meeting);
  const uplink = useUplinkStats(meeting, !disabled);
  const [flow, setFlow] = useState({}); // peerId -> {fps, stalledMs} | null (remote tiles only)
  const flowFor = useCallback((peerId) => (s) => setFlow((f) => {
    const cur = f[peerId];
    if (cur === s || (cur && s && cur.fps === s.fps && (cur.stalledMs > 2500) === (s.stalledMs > 2500))) return f;
    return { ...f, [peerId]: s };
  }), []);
  // Track settings (resolution) arrive after the first frames and change over time; poll gently.
  useEffect(() => { const id = setInterval(() => tick((t) => t + 1), 2000); return () => clearInterval(id); }, []);
  const sources = program?.sources || [];
  const lt = program?.lower_third || {};
  const fxAll = program?.source_fx || {};

  // Connected people/cameras first, then admitted guests/cameras that haven't connected yet
  // (only recent ones — stale entries from earlier sessions are hidden but can be removed).
  // Peers whose registry entry is gone (removed by the producer) are hidden even if still publishing.
  // A view-only console's own hidden-viewer peer (view_…) is not a source either.
  const connected = roster.list.filter((e) => !(e.isSelf && e.customId?.startsWith("view_")) && (e.isSelf || !e.customId || registry.size === 0 || registry.has(e.customId)));
  const connectedIds = new Set(connected.map((e) => e.customId));
  const recent = (iso) => !iso || (Date.now() - new Date(iso).getTime()) < 15 * 60 * 1000;
  const pending = [...registry.values()].filter((p) => p.status === "admitted" && !connectedIds.has(p.participant_id) && p.kind !== "host" && recent(p.admitted_at));

  if (connected.length === 0 && pending.length === 0) {
    return <p className="text-sm text-zinc-500">{disabled ? "No cameras or guests are connected yet." : "Connecting your camera… Guests and cameras will appear here as they join."}</p>;
  }

  const setFx = (pid, patch) => patchProgram({ source_fx: { [pid]: patch } });
  const removeSource = (pid, name, peer) => window.confirm(`Remove ${name} from the show?`) && run(`rm-${pid}`, async () => {
    const r = await studioApi.remove(showId, pid);
    // Belt and braces: kick the peer from the room client-side too (host permission).
    if (peer && !peer.isSelf) { try { await meeting?.participants?.kick?.(peer.peerId); } catch (e) { /* noop */ } }
    return r;
  }, `${name} removed`);

  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        {connected.map((e, i) => {
          const reg = e.customId ? registry.get(e.customId) : null;
          const pid = e.customId;
          const idx = pid ? sources.indexOf(pid) : -1;
          const onAir = idx >= 0 || (program?.layout === "speaker");
          const name = reg?.label || reg?.name || e.name || "Guest";
          const kind = reg?.kind || (e.isSelf ? "host" : "guest");
          const fx = (pid && fxAll[pid]) || {};
          const size = trackSize(e.rtk);
          const isPortrait = !!(size && size.h > size.w);
          const hasFx = !!(fx.mirror || fx.rotate || (fx.fit && fx.fit !== "cover"));
          return (
            <div key={e.peerId} className={`group relative overflow-hidden rounded-lg border transition ${idx >= 0 ? "border-[#c9772e] ring-2 ring-[#c9772e]/40" : "border-white/10"}`} data-testid={`source-${pid || e.peerId}`}>
              <button type="button" className="block w-full text-left" onClick={() => pid && !disabled && onTileClick(pid)} title={`Put on air (key ${i + 1})`} data-testid={`source-take-${pid || e.peerId}`}>
                <VideoTile rtk={e.rtk} name={name} fx={fx} showBadges onFlow={e.isSelf ? undefined : flowFor(e.peerId)} className="aspect-video w-full">
                  {idx >= 0 && (
                    <span className="absolute right-1.5 top-1.5 rounded bg-[#c9772e] px-1.5 py-0.5 text-[10px] font-bold text-white">
                      {program?.layout === "solo" ? "ON AIR" : `ON AIR ${idx + 1}`}
                    </span>
                  )}
                  {reg?.room_cam && <span className="absolute left-1.5 top-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-200" title="Room camera — no automatic name badge" data-testid={`roomcam-badge-${pid}`}>Room cam</span>}
                  {!onAir && <span className="absolute right-1.5 top-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-300 opacity-0 transition group-hover:opacity-100">Take</span>}
                  {i < 9 && <span className="absolute bottom-1.5 left-1.5 flex h-5 w-5 items-center justify-center rounded bg-black/70 font-mono text-[11px] font-bold text-zinc-200" title={`Hotkey ${i + 1}`} data-testid={`source-hotkey-${pid || e.peerId}`}>{i + 1}</span>}
                  {size && (
                    <span className={`absolute bottom-1.5 right-1.5 rounded px-1.5 py-0.5 text-[10px] font-medium ${isPortrait ? "bg-amber-500/90 text-black" : "bg-black/60 text-zinc-300"}`} title={isPortrait ? "Portrait video — ask the operator to turn the phone sideways, or set Fit to Fit" : "Incoming resolution"} data-testid={`source-res-${pid || e.peerId}`}>
                      {isPortrait ? `${size.w}×${size.h} portrait` : `${size.h}p`}
                    </span>
                  )}
                </VideoTile>
              </button>
              <div className="flex items-center gap-1 bg-[#0a0f17] px-2 py-1.5">
                <ConnectionDot entry={health[e.peerId]} flow={e.isSelf ? undefined : flow[e.peerId]} uplink={e.isSelf ? uplink : undefined} direction={e.isSelf ? "out" : "in"} className="mr-0.5" testId={`conn-${pid || e.peerId}`} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-xs font-medium">{name}{e.isSelf && <span className="text-zinc-500"> (you)</span>}</div>
                  <div className="truncate text-[10px] uppercase tracking-wider text-zinc-500">{kind}{reg?.room_cam ? " · room cam" : ""}{reg?.device ? ` · ${reg.device}` : ""}</div>
                </div>
                {pid && (
                  <button type="button" title={lt.visible && lt.participant_id === pid ? "Hide name plate" : "Name plate (click = show for a few seconds · shift-click = keep on)"} onClick={(ev) => onNamePlate(pid, ev.shiftKey)} disabled={disabled} data-testid={`nameplate-${pid}`}
                    className={`rounded p-1 ${lt.visible && lt.participant_id === pid ? "bg-[#c9772e] text-white" : "text-zinc-400 hover:bg-white/10 hover:text-white"}`}><Tag className="h-3.5 w-3.5" /></button>
                )}
                {pid && kind !== "camera" && (
                  <button type="button" disabled={disabled} data-testid={`roomcam-${pid}`}
                    title={reg?.room_cam ? "Room camera: no automatic name badge (click to turn badges back on)" : "Mark as room camera (Owl / wide shot) — never auto-show a name badge"}
                    onClick={() => run(`roomcam-${pid}`, () => studioApi.patchParticipant(showId, pid, { room_cam: !reg?.room_cam }), reg?.room_cam ? `${name}: name badge back on` : `${name} marked as room camera — no auto name badge`)}
                    className={`rounded p-1 ${reg?.room_cam ? "bg-white/15 text-white" : "text-zinc-400 hover:bg-white/10 hover:text-white"}`}><Users className="h-3.5 w-3.5" /></button>
                )}
                {!e.isSelf && e.rtk?.audioEnabled && (
                  <button type="button" title="Mute" disabled={disabled} onClick={() => { try { e.rtk.disableAudio(); } catch (err) { /* noop */ } studioApi.micControl(showId, [pid], false).catch((err) => toast.error(errMessage(err, "Not allowed"))); }} className="rounded p-1 text-zinc-400 hover:bg-white/10 hover:text-white disabled:opacity-40" data-testid={`mute-${pid}`}><MicOff className="h-3.5 w-3.5" /></button>
                )}
                {pid && (
                  <button type="button" title="Adjust picture & details" onClick={() => setAdjusting(adjusting === pid ? null : pid)} data-testid={`adjust-${pid}`}
                    className={`rounded p-1 ${adjusting === pid || hasFx ? "bg-white/15 text-white" : "text-zinc-400 hover:bg-white/10 hover:text-white"}`}><Settings2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
              {pid && adjusting === pid && (
                <div className="flex flex-wrap items-center gap-1 border-t border-white/10 bg-[#0a0f17] px-2 py-1.5" data-testid={`adjust-panel-${pid}`}>
                  <button type="button" onClick={() => setFx(pid, { mirror: !fx.mirror })} disabled={disabled} className={`inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] ${fx.mirror ? "bg-[#c9772e]/20 text-[#e6a25f]" : "text-zinc-300 hover:bg-white/10"}`} title="Mirror" data-testid={`fx-mirror-${pid}`}><FlipHorizontal2 className="h-3.5 w-3.5" /> Mirror</button>
                  <button type="button" onClick={() => setFx(pid, { rotate: ((fx.rotate || 0) + 90) % 360 })} disabled={disabled} className={`inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] ${fx.rotate ? "bg-[#c9772e]/20 text-[#e6a25f]" : "text-zinc-300 hover:bg-white/10"}`} title="Rotate 90°" data-testid={`fx-rotate-${pid}`}><RotateCw className="h-3.5 w-3.5" /> {fx.rotate ? `${fx.rotate}°` : "Rotate"}</button>
                  <button type="button" onClick={() => setFx(pid, { fit: fx.fit === "contain" ? "cover" : "contain" })} disabled={disabled} className={`inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] ${fx.fit === "contain" ? "bg-[#c9772e]/20 text-[#e6a25f]" : "text-zinc-300 hover:bg-white/10"}`} title={fx.fit === "contain" ? "Showing the whole picture (letterboxed)" : "Filling the frame (crops edges)"} data-testid={`fx-fit-${pid}`}>{fx.fit === "contain" ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />} {fx.fit === "contain" ? "Fit" : "Fill"}</button>
                  {hasFx && <button type="button" onClick={() => setFx(pid, { mirror: false, rotate: 0, fit: "cover" })} disabled={disabled} className="rounded px-1.5 py-1 text-[11px] text-zinc-400 hover:bg-white/10 hover:text-white" data-testid={`fx-reset-${pid}`}>Reset</button>}
                  <span className="mx-auto" />
                  <button type="button" title="Edit details" disabled={disabled} onClick={() => setEditing(reg || { participant_id: pid, name })} className="rounded p-1 text-zinc-400 hover:bg-white/10 hover:text-white disabled:opacity-40" data-testid={`edit-${pid}`}><SquareUser className="h-3.5 w-3.5" /></button>
                  {kind !== "host" && (
                    <button type="button" title="Remove from show" disabled={disabled} onClick={() => removeSource(pid, name, e)} className="rounded p-1 text-zinc-400 hover:bg-red-500/20 hover:text-red-300" data-testid={`remove-${pid}`}><UserMinus className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {pending.map((p) => (
          <div key={p.participant_id} className="relative flex aspect-[16/11] flex-col items-center justify-center rounded-lg border border-dashed border-white/15 p-3 text-center" data-testid={`pending-${p.participant_id}`}>
            <button type="button" title="Remove" disabled={disabled} onClick={() => run(`rm-${p.participant_id}`, () => studioApi.remove(showId, p.participant_id))} className="absolute right-1 top-1 rounded p-1 text-zinc-500 hover:bg-white/10 hover:text-white disabled:opacity-40" data-testid={`pending-remove-${p.participant_id}`}><X className="h-3 w-3" /></button>
            <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
            <div className="mt-2 truncate text-xs font-medium">{p.label || p.name}</div>
            <div className="text-[10px] text-zinc-500">admitted · connecting</div>
          </div>
        ))}
      </div>

      {editing && (
        <EditParticipant p={editing} onClose={() => setEditing(null)} onSave={async (body) => {
          const ok = await run("edit", () => studioApi.patchParticipant(showId, editing.participant_id, body), "Details saved");
          if (ok) setEditing(null);
        }} />
      )}
    </>
  );
}

function EditParticipant({ p, onClose, onSave }) {
  const [name, setName] = useState(p.name || "");
  const [title, setTitle] = useState(p.title || "");
  const [org, setOrg] = useState(p.org || "");
  const [label, setLabel] = useState(p.label || "");
  const [plates, setPlates] = useState([]);
  const [showPlates, setShowPlates] = useState(false);

  // Saved plates matching the name (library shared across shows).
  useEffect(() => {
    if (p.kind === "camera") return undefined;
    const q = name.trim();
    const id = setTimeout(() => {
      studioApi.listPlates(q.length >= 2 ? q : "").then((list) => setPlates((list || []).slice(0, 6))).catch(() => setPlates([]));
    }, 200);
    return () => clearTimeout(id);
  }, [name, p.kind]);
  const apply = (pl) => { setName(pl.name || ""); setTitle(pl.title || ""); setOrg(pl.org || ""); setShowPlates(false); };
  const exact = plates.find((pl) => pl.name.toLowerCase() === name.trim().toLowerCase() && (pl.title || "") === title && (pl.org || "") === org);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="edit-participant-dialog">
      <div className="w-full max-w-sm rounded-xl border border-white/10 bg-[#0f1520] p-5">
        <h4 className="text-sm font-semibold">Edit details</h4>
        <p className="mt-1 text-xs text-zinc-500">Used for the on-screen name plate. Saved to the plate library for next time.</p>
        <div className="mt-3 space-y-2">
          <div className="relative">
            <input value={name} onChange={(e) => { setName(e.target.value); setShowPlates(true); }} onFocus={() => setShowPlates(true)} placeholder="Name" className="w-full rounded-md border border-white/10 bg-[#0a0f17] px-3 py-2 text-sm" data-testid="edit-name" autoComplete="off" />
            {showPlates && plates.length > 0 && !exact && (
              <ul className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-md border border-white/10 bg-[#0f1520] shadow-xl" data-testid="plate-suggestions">
                <li className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Saved plates</li>
                {plates.map((pl) => (
                  <li key={pl.plate_id}>
                    <button type="button" onClick={() => apply(pl)} className="flex w-full items-start gap-2 px-3 py-1.5 text-left hover:bg-white/5" data-testid={`plate-suggestion-${pl.plate_id}`}>
                      <BookmarkCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#e6a25f]" />
                      <span className="min-w-0"><span className="block truncate text-sm">{pl.name}</span><span className="block truncate text-[11px] text-zinc-400">{[pl.title, pl.org].filter(Boolean).join(" · ")}</span></span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Founder)" className="w-full rounded-md border border-white/10 bg-[#0a0f17] px-3 py-2 text-sm" data-testid="edit-title" />
          <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Organization" className="w-full rounded-md border border-white/10 bg-[#0a0f17] px-3 py-2 text-sm" data-testid="edit-org" />
          {p.kind === "camera" && <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Camera label" className="w-full rounded-md border border-white/10 bg-[#0a0f17] px-3 py-2 text-sm" data-testid="edit-label" />}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Btn tone="ghost" onClick={onClose}>Cancel</Btn>
          <Btn tone="copper" onClick={() => onSave({ name, title, org, ...(p.kind === "camera" ? { label } : {}) })} data-testid="edit-save">Save</Btn>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Self (host) controls                                                */
/* ------------------------------------------------------------------ */
function SelfControls({ meeting, joined }) {
  const [devices, setDevices] = useState({ video: [], audio: [] });
  const [current, setCurrent] = useState({ video: "", audio: "" });
  const [tick, setTick] = useState(0);

  const refresh = useCallback(async () => {
    if (!meeting) return;
    try {
      const [video, audio] = await Promise.all([meeting.self.getVideoDevices(), meeting.self.getAudioDevices()]);
      setDevices({ video: video || [], audio: audio || [] });
      const cur = meeting.self.getCurrentDevices?.() || {};
      setCurrent({ video: cur.video?.deviceId || "", audio: cur.audio?.deviceId || "" });
    } catch (e) { /* noop */ }
  }, [meeting]);

  useEffect(() => {
    if (!meeting || !joined) return undefined;
    refresh();
    const h = () => { refresh(); setTick((t) => t + 1); };
    meeting.self.on("deviceListUpdate", h);
    meeting.self.on("deviceUpdate", h);
    meeting.self.on("videoUpdate", h);
    meeting.self.on("audioUpdate", h);
    return () => { try { meeting.self.off("deviceListUpdate", h); meeting.self.off("deviceUpdate", h); meeting.self.off("videoUpdate", h); meeting.self.off("audioUpdate", h); } catch (e) { /* noop */ } };
  }, [meeting, joined, refresh]);

  if (!meeting) return null;
  const self = meeting.self;
  const pick = async (kind, deviceId) => {
    const d = devices[kind].find((x) => x.deviceId === deviceId);
    if (!d) return;
    try { await self.setDevice(d); setCurrent((c) => ({ ...c, [kind]: deviceId })); toast.success(`${kind === "video" ? "Camera" : "Microphone"} switched`); } catch (e) { toast.error("Could not switch device"); }
  };
  return (
    <Section title="Your camera & mic" testId="self-controls" right={<button type="button" onClick={refresh} className="text-zinc-500 hover:text-white" title="Refresh devices"><RefreshCw className="h-3.5 w-3.5" /></button>}>
      <div className="space-y-2" data-tick={tick}>
        <div className="flex items-center gap-2">
          <Camera className="h-4 w-4 shrink-0 text-zinc-400" />
          <select value={current.video} onChange={(e) => pick("video", e.target.value)} className="min-w-0 flex-1 rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="select-camera">
            {devices.video.length === 0 && <option value="">No camera found</option>}
            {devices.video.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || "Camera"}</option>)}
          </select>
          <Btn tone={self.videoEnabled ? "ghost" : "red"} className="px-2 py-1.5" onClick={() => (self.videoEnabled ? self.disableVideo() : self.enableVideo())} title={self.videoEnabled ? "Turn camera off" : "Turn camera on"} data-testid="toggle-self-video">
            {self.videoEnabled ? <Camera className="h-4 w-4" /> : <CameraOff className="h-4 w-4" />}
          </Btn>
        </div>
        <div className="flex items-center gap-2">
          <Mic className="h-4 w-4 shrink-0 text-zinc-400" />
          <select value={current.audio} onChange={(e) => pick("audio", e.target.value)} className="min-w-0 flex-1 rounded-md border border-white/10 bg-[#0a0f17] px-2 py-1.5 text-xs" data-testid="select-mic">
            {devices.audio.length === 0 && <option value="">No microphone found</option>}
            {devices.audio.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || "Microphone"}</option>)}
          </select>
          <Btn tone={self.audioEnabled ? "ghost" : "red"} className="px-2 py-1.5" onClick={() => (self.audioEnabled ? self.disableAudio() : self.enableAudio())} title={self.audioEnabled ? "Mute" : "Unmute"} data-testid="toggle-self-audio">
            {self.audioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
          </Btn>
        </div>
        <p className="text-[11px] text-zinc-500">Plug in the Owl (or any USB camera) and pick it here — switching is instant and keeps you on air.</p>
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Add a phone / wireless camera (QR always visible)                    */
/* ------------------------------------------------------------------ */
function CameraInvite({ secrets, showTitle, cameraCount }) {
  const [big, setBig] = useState(false);
  useEffect(() => {
    if (!big) return undefined;
    const h = (e) => { if (e.key === "Escape") setBig(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [big]);
  if (!secrets?.camera_path) return null;
  const url = `${window.location.origin}${secrets.camera_path}`;
  return (
    <Section title="Phone & wireless cameras" testId="camera-invite" right={cameraCount > 0 && <span className="text-[11px] text-zinc-500" data-testid="camera-invite-count">{cameraCount} connected</span>}>
      <div className="flex gap-3">
        <button type="button" onClick={() => setBig(true)} className="shrink-0 rounded-lg bg-white p-1.5 transition hover:ring-2 hover:ring-[#c9772e]" title="Show full-screen for scanning across the room" data-testid="camera-qr-small">
          <QRCodeSVG value={url} size={92} />
        </button>
        <div className="min-w-0 flex-1 text-xs">
          <div className="flex items-center gap-1.5 font-semibold text-zinc-200"><Smartphone className="h-3.5 w-3.5 text-[#e6a25f]" /> Scan to add a camera</div>
          <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">Any phone becomes a switchable camera: scan, pick a label, tap <span className="text-zinc-300">Start camera</span>. It shows up here in a few seconds — click its tile (or press its number) to take it.</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Btn tone="ghost" className="px-2 py-1 text-[11px]" onClick={() => setBig(true)} data-testid="camera-qr-big"><ScanLine className="h-3.5 w-3.5" /> Full-screen QR</Btn>
            <Btn tone="ghost" className="px-2 py-1 text-[11px]" onClick={() => copyText(url, "Camera link")} data-testid="copy-camera"><Copy className="h-3.5 w-3.5" /> Copy link</Btn>
          </div>
        </div>
      </div>
      {big && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0a0f17]/95 p-6 text-center" onClick={() => setBig(false)} data-testid="camera-qr-modal">
          <div className="rounded-3xl bg-white p-6 shadow-2xl"><QRCodeSVG value={url} size={Math.min(440, Math.floor(window.innerHeight * 0.55))} /></div>
          <div className="mt-6 flex items-center gap-2 text-2xl font-semibold"><QrCode className="h-6 w-6 text-[#e6a25f]" /> Scan to join as a camera</div>
          <div className="mt-2 text-sm text-zinc-400">{showTitle} · point your phone camera at the code, then tap the link</div>
          <code className="mt-4 rounded bg-white/5 px-3 py-1.5 text-xs text-zinc-300">{url}</code>
          <div className="mt-6 text-xs text-zinc-500">Click anywhere or press Esc to close</div>
        </div>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Room monitor link (venue TVs / confidence monitor)                 */
/* ------------------------------------------------------------------ */
function MonitorInvite({ secrets, setSecrets, showId, showTitle, run }) {
  const [big, setBig] = useState(false);
  useEffect(() => {
    if (!big) return undefined;
    const h = (e) => { if (e.key === "Escape") setBig(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [big]);
  if (!secrets?.monitor_path) return null;
  const url = `${window.location.origin}${secrets.monitor_path}`;
  const rotate = () => window.confirm("Make a new monitor link? The old link stops working (screens already open keep running until they reload).")
    && run("monitor-rotate", async () => {
      const r = await studioApi.rotateMonitorKey(showId);
      setSecrets((s) => ({ ...s, monitor_key: r.monitor_key, monitor_path: r.monitor_path }));
    }, "New monitor link ready");
  return (
    <Section title="Room monitors" testId="monitor-invite">
      <div className="flex gap-3">
        <button type="button" onClick={() => setBig(true)} className="shrink-0 self-start rounded-lg bg-white p-1.5 transition hover:ring-2 hover:ring-[#c9772e]" title="Show full-screen for scanning" data-testid="monitor-qr-small">
          <QRCodeSVG value={url} size={92} />
        </button>
        <div className="min-w-0 flex-1 text-xs">
          <div className="flex items-center gap-1.5 font-semibold text-zinc-200"><Tv className="h-3.5 w-3.5 text-[#e6a25f]" /> TVs in the room: use this, not the public stream</div>
          <p className="mt-1 text-[11px] leading-relaxed text-zinc-500">Open this link on any TV or laptop browser and it shows the program straight from the room — about <span className="text-zinc-300">0.3 s</span> behind real life. The public stream is 3–30 s behind, so never feed room screens from it. Sound is off by default; tap once to turn it on. No sign-in needed.</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-medium text-zinc-100 hover:bg-white/10" data-testid="open-monitor"><ExternalLink className="h-3.5 w-3.5" /> Open monitor</a>
            <Btn tone="ghost" className="px-2 py-1 text-[11px]" onClick={() => copyText(url, "Monitor link")} data-testid="copy-monitor"><Copy className="h-3.5 w-3.5" /> Copy link</Btn>
            <Btn tone="ghost" className="px-2 py-1 text-[11px]" onClick={() => setBig(true)} data-testid="monitor-qr-big"><ScanLine className="h-3.5 w-3.5" /> Full-screen QR</Btn>
            <Btn tone="ghost" className="px-2 py-1 text-[11px] text-zinc-400" onClick={rotate} title="Revoke: old link stops working" data-testid="rotate-monitor"><RefreshCw className="h-3.5 w-3.5" /> New link</Btn>
          </div>
        </div>
      </div>
      <MonitorHealth showId={showId} />
      {big && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0a0f17]/95 p-6 text-center" onClick={() => setBig(false)} data-testid="monitor-qr-modal">
          <div className="rounded-3xl bg-white p-6 shadow-2xl"><QRCodeSVG value={url} size={Math.min(440, Math.floor(window.innerHeight * 0.55))} /></div>
          <div className="mt-6 flex items-center gap-2 text-2xl font-semibold"><Tv className="h-6 w-6 text-[#e6a25f]" /> Room monitor link</div>
          <div className="mt-2 text-sm text-zinc-400">{showTitle} · open on the TV's browser for a real-time picture</div>
          <code className="mt-4 rounded bg-white/5 px-3 py-1.5 text-xs text-zinc-300">{url}</code>
          <div className="mt-6 text-xs text-zinc-500">Click anywhere or press Esc to close</div>
        </div>
      )}
    </Section>
  );
}

/** "Broadcast is ~N s behind" — polled from the live HLS playlist while streaming. */
function BroadcastDelayPill({ showId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => studioApi.broadcastDelay(showId).then((r) => { if (alive) setD(r); }).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [showId]);
  if (!d?.live || d.seconds == null) return null;
  const tip = `Viewers of the public stream see everything about ${d.seconds} s after it happens (${d.method === "measured" ? "measured from the live playlist" : "typical for this stream type"}${d.low_latency ? ", low-latency mode" : ""}). Give online questions that long to arrive. Room TVs on the monitor link are ~0.3 s behind.`;
  return (
    <Pill tone={d.seconds > 12 ? "amber" : "zinc"} testId="pill-broadcast-delay">
      <span title={tip} className="inline-flex items-center gap-1"><Timer className="h-3 w-3" /> Broadcast ~{d.seconds} s behind</span>
    </Pill>
  );
}

/* ------------------------------------------------------------------ */
/* Invite links                                                        */
/* ------------------------------------------------------------------ */
function InvitePanel({ secrets, showTitle }) {
  const [qr, setQr] = useState(null);
  if (!secrets?.guest_path) return null;
  const origin = window.location.origin;
  const guest = `${origin}${secrets.guest_path}`;
  const Row = ({ label, hint, url, id }) => (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid={`invite-${id}`}>
      <div className="text-xs font-semibold text-zinc-200">{label}</div>
      <div className="text-[11px] text-zinc-500">{hint}</div>
      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-[#0a0f17] px-2 py-1.5 text-[11px] text-zinc-300">{url}</code>
        <Btn tone="ghost" className="px-2 py-1.5" onClick={() => copyText(url, label)} title="Copy" data-testid={`copy-${id}`}><Copy className="h-3.5 w-3.5" /></Btn>
        <Btn tone="ghost" className="px-2 py-1.5 text-xs" onClick={() => setQr(qr === id ? null : { id, url, label })} data-testid={`qr-${id}`}>QR</Btn>
      </div>
      {qr?.id === id && (
        <div className="mt-3 flex items-center gap-4 rounded-lg bg-white p-3">
          <QRCodeSVG value={url} size={132} />
          <div className="text-xs text-zinc-700">
            <div className="font-semibold">{label}</div>
            <div className="mt-1">Scan with a phone to open.</div>
            <div className="mt-1 text-zinc-500">{showTitle}</div>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <Section title="Invite" testId="invite-section">
      <div className="space-y-3">
        <Row id="guest" label="Guest link" hint="Guests enter their name, check their camera, and wait in the lobby until you admit them." url={guest} />
      </div>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Audience questions (Hub Q&A) -> one click on air                    */
/* ------------------------------------------------------------------ */
function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function QuestionsPanel({ showId, show, program, tick, run, setShow, disabled }) {
  const [questions, setQuestions] = useState(null);
  const [showAnswered, setShowAnswered] = useState(false);
  const onAirId = program?.question?.visible ? program?.question?.question_id : null;

  const load = useCallback(() => {
    studioApi.listQuestions(showId).then(setQuestions).catch(() => {});
  }, [showId]);

  // Initial + on every "questions_changed" nudge from the Hub + a slow safety poll.
  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => { const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  const applyProgramQuestion = (q) => setShow((s) => (s ? { ...s, program: { ...s.program, question: q } } : s));

  const air = (q) => run(`air-${q.question_id}`, async () => {
    if (onAirId === q.question_id) { applyProgramQuestion(await studioApi.clearQuestion(showId)); }
    else { applyProgramQuestion(await studioApi.airQuestion(showId, q.question_id)); }
    load();
  });
  const setStatus = (q, status) => run(`qs-${q.question_id}`, async () => {
    await studioApi.setQuestionStatus(showId, q.question_id, status);
    load();
  }, status === "answered" ? "Marked answered" : status === "dismissed" ? "Question dismissed" : "Reopened");

  const open = (questions || []).filter((q) => q.status === "open");
  const answered = (questions || []).filter((q) => q.status === "answered");
  const list = showAnswered ? [...open, ...answered] : open;

  return (
    <Section
      title={`Audience questions${questions ? ` (${open.length} open)` : ""}`}
      testId="questions-section"
      right={(
        <div className="flex items-center gap-3 text-[11px]">
          {answered.length > 0 && (
            <button type="button" onClick={() => setShowAnswered((v) => !v)} className="text-zinc-400 hover:text-white" data-testid="toggle-answered">
              {showAnswered ? "Hide answered" : `Show answered (${answered.length})`}
            </button>
          )}
          {show?.hub_session_id && (
            <Link to={`/portal/hub/${show.hub_session_id}`} target="_blank" className="inline-flex items-center gap-1 text-zinc-400 hover:text-white" data-testid="open-hub-qa">Hub Q&A <ExternalLink className="h-3 w-3" /></Link>
          )}
          <button type="button" onClick={load} className="text-zinc-500 hover:text-white" title="Refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
        </div>
      )}
    >
      {onAirId && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-[#c9772e]/40 bg-[#c9772e]/10 px-3 py-2 text-xs" data-testid="question-on-air-bar">
          <span className="inline-flex items-center gap-2 text-[#e6a25f]"><Eye className="h-3.5 w-3.5" /> A question is on screen</span>
          <button type="button" onClick={() => run("clear-q", async () => { applyProgramQuestion(await studioApi.clearQuestion(showId)); load(); })} className="rounded bg-white/10 px-2 py-1 text-[11px] font-medium hover:bg-white/20" data-testid="question-clear">Take off screen</button>
        </div>
      )}
      {questions === null ? (
        <p className="text-sm text-zinc-500">Loading questions…</p>
      ) : list.length === 0 ? (
        <div className="flex items-start gap-3 rounded-lg border border-dashed border-white/10 p-4 text-sm text-zinc-500" data-testid="questions-empty">
          <MessageCircleQuestion className="mt-0.5 h-5 w-5 shrink-0 text-zinc-600" />
          <div>No open questions yet. Members ask in the Hub — they appear here instantly, most upvoted first. Click <span className="text-zinc-300">On air</span> to put one on screen.</div>
        </div>
      ) : (
        <ul className="max-h-80 space-y-2 overflow-y-auto pr-1" data-testid="questions-list">
          {list.map((q) => {
            const onAir = q.question_id === onAirId;
            return (
              <li key={q.question_id} className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 transition ${onAir ? "border-[#c9772e] bg-[#c9772e]/10" : q.status === "answered" ? "border-white/5 bg-white/[0.02] opacity-70" : "border-white/10 bg-white/[0.03]"}`} data-testid={`question-${q.question_id}`}>
                <div className="flex w-10 shrink-0 flex-col items-center rounded-md bg-[#0a0f17] py-1 text-zinc-300" title="Upvotes">
                  <ThumbsUp className="h-3 w-3" />
                  <span className="text-xs font-semibold" data-testid={`question-votes-${q.question_id}`}>{q.upvote_count}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-snug text-zinc-100">{q.text}</p>
                  <p className="mt-0.5 text-[11px] text-zinc-500">{q.author} · {timeAgo(q.created_at)}{q.status === "answered" && <span className="ml-2 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">answered</span>}{onAir && <span className="ml-2 rounded bg-[#c9772e] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">on screen</span>}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Btn tone={onAir ? "copper" : "ghost"} className="px-2.5 py-1.5 text-xs" disabled={disabled} onClick={() => air(q)} title={onAir ? "Take off screen" : "Show on screen"} data-testid={`question-air-${q.question_id}`}>
                    {onAir ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}{onAir ? "Off air" : "On air"}
                  </Btn>
                  {q.status === "open" ? (
                    <button type="button" title="Mark answered" onClick={() => setStatus(q, "answered")} className="rounded p-1.5 text-zinc-400 hover:bg-emerald-500/20 hover:text-emerald-300" data-testid={`question-done-${q.question_id}`}><CheckCheck className="h-4 w-4" /></button>
                  ) : (
                    <button type="button" title="Reopen" onClick={() => setStatus(q, "open")} className="rounded p-1.5 text-zinc-400 hover:bg-white/10 hover:text-white" data-testid={`question-reopen-${q.question_id}`}><RefreshCw className="h-3.5 w-3.5" /></button>
                  )}
                  <button type="button" title="Dismiss" onClick={() => setStatus(q, "dismissed")} className="rounded p-1.5 text-zinc-400 hover:bg-red-500/20 hover:text-red-300" data-testid={`question-dismiss-${q.question_id}`}><X className="h-4 w-4" /></button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Recording status                                                    */
/* ------------------------------------------------------------------ */
function RecordingsSection({ show, secrets, onRefresh, busy }) {
  const rec = show?.recording || {};
  const live = show?.livestream || {};
  const stateText = {
    idle: "Not recording yet", starting: "Recorder is starting up (10–20s)…", recording: "Recording in progress",
    uploading: "Stopped — Cloudflare is finalizing the MP4…", imported: "Saved to your Recordings library", failed: rec.error || "Recording failed",
  }[rec.status] || rec.status;
  return (
    <Section title="Recording & stream" testId="recording-section" right={<button type="button" onClick={onRefresh} disabled={busy} className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-white"><RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Refresh</button>}>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-zinc-300"><Film className="h-3.5 w-3.5 text-[#e6a25f]" /> Cloud recording</div>
          <p className="mt-1 text-sm" data-testid="recording-state">{stateText}</p>
          {rec.status === "imported" && rec.recording_id && (
            <Link to={`/admin/recordings/${rec.recording_id}`} className="mt-2 inline-flex items-center gap-1 text-xs text-[#e6a25f] hover:underline" data-testid="open-recording">Open in library <ExternalLink className="h-3 w-3" /></Link>
          )}
          {rec.download_url && rec.status !== "imported" && (
            <a href={rec.download_url} className="mt-2 inline-flex items-center gap-1 text-xs text-[#e6a25f] hover:underline" target="_blank" rel="noreferrer">Download MP4 <ExternalLink className="h-3 w-3" /></a>
          )}
        </div>
        <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-zinc-300"><Radio className="h-3.5 w-3.5 text-[#e6a25f]" /> Live to members</div>
          <p className="mt-1 text-sm" data-testid="live-state">
            {{ idle: "Not streaming", starting: "Connecting to Cloudflare Stream…", live: "Streaming live — members can watch in the Hub", ended: "Stream ended", failed: live.error || "Stream failed" }[live.status] || live.status}
          </p>
          {show?.hub_session_id && (
            <Link to={`/portal/hub/${show.hub_session_id}`} target="_blank" className="mt-2 inline-flex items-center gap-1 text-xs text-[#e6a25f] hover:underline" data-testid="open-hub">Open member Hub <ExternalLink className="h-3 w-3" /></Link>
          )}
          {secrets?.rtmps?.key && (
            <details className="mt-2 text-[11px] text-zinc-500">
              <summary className="cursor-pointer">OBS / hardware encoder details</summary>
              <div className="mt-1 break-all">Server: {secrets.rtmps.url}<br />Key: {secrets.rtmps.key}</div>
            </details>
          )}
        </div>
      </div>
    </Section>
  );
}
